import React, { useMemo } from 'react';
import { makeStyles, useTheme } from '@material-ui/core/styles';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Card,
  CardContent,
  LinearProgress,
  Chip,
  IconButton,
  Tooltip,
  Avatar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Tabs,
  Tab,
  FormControl,
  Select,
  MenuItem,
  InputLabel,
} from '@material-ui/core';
import {
  Link as LinkIcon,
  CheckCircle,
  Warning,
  Security,
  TrendingUp,
  Devices,
  Refresh,
  InfoOutlined,
  Schedule,
  School,
  PlayArrow,
  CheckCircleOutline,
} from '@material-ui/icons';
import { Chart } from 'react-google-charts';
import useChangePageTitle from '../CustomHooks/useChangePageTitle';

const useStyles = makeStyles((theme) => ({
  root: {
    flexGrow: 1,
    padding: theme.spacing(3),
    background: theme.palette.type === 'dark' ? theme.palette.background.default : 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
    minHeight: '100vh',
  },
  controlBar: {
    background: theme.palette.type === 'dark' ? theme.palette.background.paper : 'rgba(255, 255, 255, 0.95)',
    backdropFilter: 'blur(10px)',
    borderRadius: 12,
    padding: theme.spacing(2),
    marginBottom: theme.spacing(3),
    boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
    border: '1px solid rgba(255,255,255,0.2)',
  },
  heroCard: {
    background: 'linear-gradient(135deg, #061739 0%, #0055A2 50%, #428BC9 100%)',
    color: 'white',
    borderRadius: 20,
    padding: theme.spacing(3),
    marginBottom: theme.spacing(3),
    position: 'relative',
    overflow: 'hidden',
    boxShadow: '0 8px 32px rgba(0, 85, 162, 0.3)',
  },
  kpiCard: {
    borderRadius: 12,
    height: '100%',
    background: theme.palette.type === 'dark' ? theme.palette.background.paper : 'rgba(255, 255, 255, 0.95)',
    backdropFilter: 'blur(10px)',
    border: '1px solid rgba(255,255,255,0.2)',
    boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
    transition: 'all 0.3s ease',
    '&:hover': {
      transform: 'translateY(-2px)',
      boxShadow: '0 6px 30px rgba(0,0,0,0.12)',
    },
  },
  chartCard: {
    borderRadius: 16,
    background: theme.palette.type === 'dark' ? theme.palette.background.paper : 'rgba(255, 255, 255, 0.95)',
    backdropFilter: 'blur(10px)',
    border: '1px solid rgba(255,255,255,0.2)',
    boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
    padding: theme.spacing(3),
  },
  progressRing: {
    position: 'relative',
    width: 140,
    height: 140,
    margin: '0 auto',
  },
  riskChip: {
    fontWeight: 600,
    fontSize: '0.9rem',
    padding: theme.spacing(2, 3),
  },
  tableContainer: {
    maxHeight: 500,
    overflowY: 'auto',
    '& thead': {
      position: 'sticky',
      top: 0,
      zIndex: 2,
    },
    '& thead tr': {
      background: 'linear-gradient(90deg, #0a1e42 0%, #0b5fad 50%, #3a8ac4 100%)',
    },
    '& thead th': {
      backgroundColor: 'transparent',
      color: 'white',
      fontWeight: 600,
      borderBottom: 'none',
    },
  },
  linkCell: {
    maxWidth: 300,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    cursor: 'pointer',
    '&:hover': {
      textDecoration: 'underline',
      color: '#0055A2',
    },
  },
  trainingCard: {
    borderRadius: 12,
    background: theme.palette.type === 'dark' ? theme.palette.background.paper : 'rgba(255, 255, 255, 0.95)',
    backdropFilter: 'blur(10px)',
    border: '1px solid rgba(255,255,255,0.2)',
    boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
    padding: theme.spacing(2),
    marginBottom: theme.spacing(2),
    cursor: 'pointer',
    transition: 'all 0.3s ease',
    '&:hover': {
      transform: 'translateY(-2px)',
      boxShadow: '0 6px 30px rgba(0,0,0,0.12)',
      borderColor: '#0055A2',
    },
  },
  pageTitle: {
    fontWeight: 700,
    color: theme.palette.type === 'dark' ? theme.palette.text.primary : '#1a237e',
  },
}));

const ModernUserLinkHistory = (props) => {
  useChangePageTitle('History');
  const classes = useStyles();
  const theme = useTheme();
  const isDark = theme.palette.type === 'dark';
  const [tabValue, setTabValue] = React.useState(0);
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(10);

  const {
    userBoxData,
    linkHistoryData,
    trainingData,
    isLoading,
    hasTraining,
    preferredLanguage,
    getTranslation,
    accessLevel,
    history,
    viewMode,
    onViewModeChange,
    refreshTime,
  } = props;

  // Calculate user risk metrics
  const userMetrics = useMemo(() => {
    if (!userBoxData) return null;

    const totalSeen = userBoxData.safeLinks + userBoxData.unknownLinks + userBoxData.unsafeLinks;
    const riskScore = userBoxData.userRiskScore || 0;

    let riskLevelKey = 'Low Risk';
    let riskColor = '#4caf50';
    if (riskScore >= 86) {
      riskLevelKey = 'High Risk';
      riskColor = '#f44336';
    } else if (riskScore >= 61) {
      riskLevelKey = 'Medium Risk';
      riskColor = '#ff9800';
    }

    return {
      totalSeen,
      safeCount: userBoxData.safeLinks || 0,
      unknownCount: userBoxData.unknownLinks || 0,
      unsafeCount: userBoxData.unsafeLinks || 0,
      riskScore,
      riskLevelKey,
      riskColor,
      devices: userBoxData.deviceList || [],
      safePercent: totalSeen > 0 ? Math.round((userBoxData.safeLinks / totalSeen) * 100) : 0,
      unsafePercent: totalSeen > 0 ? Math.round((userBoxData.unsafeLinks / totalSeen) * 100) : 0,
    };
  }, [userBoxData]);

  const newTableData = [];
  newTableData.push(props.linkHistoryData.linkHistory);

  // Process link history data for table
  const tableData = useMemo(() => {
    if (!newTableData) return { safe: [], unknown: [], unsafe: [] };
    return newTableData[0];
    //linkHistoryData.linkHistory[0];
  }, [linkHistoryData]);

  // Get current tab data
  const getCurrentTabData = () => {
    const tabNames = ['safe', 'unknown', 'unsafe'];
    return tableData[tabNames[tabValue]] || [];
  };

  const safetyMap = { 0: 'safe', 1: 'unknown' };
  const safety = safetyMap[tabValue] ?? 'unsafe';

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleLinkClick = (linkDetails, linkSafety) => {
    const pathname = accessLevel === 'User' ? '/Dashboard/AnalyzeLink' : '/Dashboard/AnalyzeLinkAdmin';
    history.push({
      pathname,
      search: '?pad=&link=' + encodeURIComponent(linkDetails),
      state: { safety: linkSafety },
    });
  };

  const handleRefresh = () => {
    window.location.reload();
  };

  const formatNumber = (num) => {
    if (num >= 1000000) return (num / 1000000).toFixed(1) + 'M';
    if (num >= 1000) return (num / 1000).toFixed(1) + 'k';
    return num;
  };

  const handleTrainingClick = (modId) => {
    history.push('/Dashboard/Training?pad=&id=' + modId);
    window.scrollTo(0, 0);
  };

  const handleGoToTraining = () => {
    history.push('/Dashboard/TrainingSuite');
  };

  // Process training data
  const trainingModules = React.useMemo(() => {
    if (!trainingData?.trainingItems) return { required: [], suggested: [], completed: [] };

    const required = trainingData.trainingItems.filter((item) => item.status === 'required');
    const suggested = trainingData.trainingItems.filter((item) => item.status === 'suggested');
    const completed = trainingData.trainingItems.filter((item) => item.progress === 100 || item.status === 'Completed');

    return { required, suggested, completed };
  }, [trainingData]);

  if (isLoading || !userMetrics) {
    return (
      <Box className={classes.root}>
        <Typography variant="h4">{getTranslation('Loading...', preferredLanguage)}</Typography>
      </Box>
    );
  }

  return (
    <div className={classes.root}>
      {/* Control Header */}
      <Paper className={classes.controlBar}>
        <Box display="flex" alignItems="center" justifyContent="space-between" flexWrap="wrap">
          <Typography
            variant="h4"
            className={classes.pageTitle}
          >
            {getTranslation('My History', preferredLanguage)}
          </Typography>

          <Box display="flex" alignItems="center" style={{ gap: 16 }}>
            <FormControl size="small" variant="outlined" style={{ minWidth: 120 }}>
              <InputLabel id="date-range-label">{getTranslation('Date Range', preferredLanguage)}</InputLabel>
              <Select
                labelId="date-range-label"
                value="30days"
                label={getTranslation('Date Range', preferredLanguage)}
              >
                <MenuItem value="7days">{getTranslation('7 Days', preferredLanguage)}</MenuItem>
                <MenuItem value="30days">{getTranslation('30 Days', preferredLanguage)}</MenuItem>
                <MenuItem value="90days">{getTranslation('90 Days', preferredLanguage)}</MenuItem>
              </Select>
            </FormControl>

            {onViewModeChange && (
              <FormControl size="small" variant="outlined" style={{ minWidth: 120 }}>
                <InputLabel id="view-mode-label">{getTranslation('View Mode', preferredLanguage)}</InputLabel>
                <Select
                  labelId="view-mode-label"
                  value={viewMode || 'modern'}
                  onChange={(e) => onViewModeChange(e.target.value)}
                  label={getTranslation('View Mode', preferredLanguage)}
                >
                  <MenuItem value="classic">{getTranslation('Classic', preferredLanguage)}</MenuItem>
                  <MenuItem value="modern">{getTranslation('Modern', preferredLanguage)}</MenuItem>
                </Select>
              </FormControl>
            )}

            <Box display="flex" alignItems="center" style={{ gap: 8 }}>
              <Tooltip title={getTranslation('Refresh Data', preferredLanguage)}>
                <IconButton
                  onClick={handleRefresh}
                  color="primary"
                  size="small"
                  style={{ backgroundColor: 'rgba(25, 118, 210, 0.08)' }}
                >
                  <Refresh fontSize="small" />
                </IconButton>
              </Tooltip>
              <Typography variant="caption" color="textSecondary">
                {refreshTime?.toLocaleTimeString() || new Date().toLocaleTimeString()}
              </Typography>
            </Box>
          </Box>
        </Box>
      </Paper>

      {/* Hero Risk Card */}
      <Card className={classes.heroCard}>
        <Grid container spacing={3} alignItems="center">
          <Grid item xs={12} md={8}>
            <Box display="flex" alignItems="center" marginBottom={2}>
              <Security style={{ fontSize: 32, marginRight: 12 }} />
              <Typography variant="h4" style={{ fontWeight: 700 }}>
                {getTranslation('user risk', preferredLanguage)}
              </Typography>
              <Tooltip
                title={getTranslation(
                  "User risk is an indication of how safe you are based on the relative safety of links you've encountered in the past 30 days.",
                  preferredLanguage
                )}
                arrow
              >
                <InfoOutlined style={{ marginLeft: 8, fontSize: 20 }} />
              </Tooltip>
            </Box>

            <Typography variant="h2" style={{ fontWeight: 700, marginBottom: 8 }}>
              {userMetrics.riskScore}%
            </Typography>

            <Chip
              label={getTranslation(userMetrics.riskLevelKey, preferredLanguage)}
              className={classes.riskChip}
              style={{
                backgroundColor: userMetrics.riskColor + '20',
                color: userMetrics.riskColor,
                border: `2px solid ${userMetrics.riskColor}40`,
              }}
            />

            <Box marginTop={2}>
              <Typography variant="body1" style={{ opacity: 0.9 }}>
                {userMetrics.totalSeen} {getTranslation('total links analyzed', preferredLanguage)}
              </Typography>
              <Typography variant="body2" style={{ opacity: 0.7 }}>
                {userMetrics.safePercent}% {getTranslation('safe', preferredLanguage)} • {userMetrics.unsafePercent}%{' '}
                {getTranslation('unsafe', preferredLanguage)}
              </Typography>
            </Box>
          </Grid>

          <Grid item xs={12} md={4}>
            <Box display="flex" justifyContent="center">
              <div className={classes.progressRing}>
                <svg width="140" height="140">
                  <circle cx="70" cy="70" r="60" fill="none" stroke="rgba(255,255,255,0.3)" strokeWidth="10" />
                  <circle
                    cx="70"
                    cy="70"
                    r="60"
                    fill="none"
                    stroke="#fff"
                    strokeWidth="10"
                    strokeDasharray={`${userMetrics.safePercent * 3.77} 377`}
                    strokeDashoffset="94.25"
                    transform="rotate(-90 70 70)"
                  />
                </svg>
              </div>
            </Box>
          </Grid>
        </Grid>
      </Card>

      {/* KPI Cards */}
      <Grid container spacing={3} style={{ marginBottom: 24 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card className={classes.kpiCard}>
            <CardContent>
              <Box display="flex" alignItems="center" marginBottom={2}>
                <LinkIcon style={{ color: '#0055A2', marginRight: 8 }} />
                <Typography variant="h6" style={{ fontWeight: 600 }}>
                  {getTranslation('links seen', preferredLanguage)}
                </Typography>
              </Box>
              <Typography variant="h3" style={{ fontWeight: 700, color: '#0055A2' }}>
                {formatNumber(userMetrics.totalSeen)}
              </Typography>
              <Typography variant="body2" color="textSecondary" style={{ marginTop: 8 }}>
                {getTranslation('Past 30 days', preferredLanguage)}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card className={classes.kpiCard}>
            <CardContent>
              <Box display="flex" alignItems="center" marginBottom={2}>
                <CheckCircle style={{ color: '#4caf50', marginRight: 8 }} />
                <Typography variant="h6" style={{ fontWeight: 600, color: '#4caf50' }}>
                  {getTranslation('safe links', preferredLanguage)}
                </Typography>
              </Box>
              <Typography variant="h3" style={{ fontWeight: 700, color: '#4caf50' }}>
                {formatNumber(userMetrics.safeCount)}
              </Typography>
              <LinearProgress
                variant="determinate"
                value={userMetrics.safePercent}
                style={{ marginTop: 12, height: 6, borderRadius: 3, backgroundColor: 'rgba(76, 175, 80, 0.2)' }}
              />
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card className={classes.kpiCard}>
            <CardContent>
              <Box display="flex" alignItems="center" marginBottom={2}>
                <Warning style={{ color: '#f44336', marginRight: 8 }} />
                <Typography variant="h6" style={{ fontWeight: 600, color: '#f44336' }}>
                  {getTranslation('unsafe links', preferredLanguage)}
                </Typography>
              </Box>
              <Typography variant="h3" style={{ fontWeight: 700, color: '#f44336' }}>
                {formatNumber(userMetrics.unsafeCount)}
              </Typography>
              <LinearProgress
                variant="determinate"
                value={userMetrics.unsafePercent}
                style={{ marginTop: 12, height: 6, borderRadius: 3, backgroundColor: 'rgba(244, 67, 54, 0.2)' }}
                color="secondary"
              />
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card className={classes.kpiCard}>
            <CardContent>
              <Box display="flex" alignItems="center" marginBottom={2}>
                <Devices style={{ color: '#428BC9', marginRight: 8 }} />
                <Typography variant="h6" style={{ fontWeight: 600, color: '#428BC9' }}>
                  {getTranslation('Active Devices', preferredLanguage)}
                </Typography>
              </Box>
              <Typography variant="h3" style={{ fontWeight: 700, color: '#428BC9' }}>
                {userMetrics.devices.length}
              </Typography>
              <Box marginTop={1}>
                {userMetrics.devices.slice(0, 2).map((device, index) => (
                  <Chip
                    key={index}
                    label={device.windowsUsername || `${getTranslation('Device', preferredLanguage)} ${index + 1}`}
                    size="small"
                    style={{ margin: 2, fontSize: '0.7rem' }}
                  />
                ))}
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Training Section - Only show if training data exists */}
      {hasTraining && trainingData?.trainingItems && trainingData.trainingItems.length > 0 && (
        <Grid container spacing={3} style={{ marginBottom: 24 }}>
          <Grid item xs={12}>
            <Paper className={classes.chartCard}>
              <Box display="flex" justifyContent="space-between" alignItems="center" marginBottom={2}>
                <Typography variant="h5" style={{ fontWeight: 600 }}>
                  <School style={{ marginRight: 8, verticalAlign: 'middle' }} />
                  {getTranslation('training', preferredLanguage)}
                </Typography>
                <Chip
                  label={getTranslation('go to training', preferredLanguage)}
                  onClick={handleGoToTraining}
                  color="primary"
                  icon={<School />}
                  style={{ cursor: 'pointer', fontWeight: 600 }}
                />
              </Box>

              <Grid container spacing={2}>
                {/* Required Training */}
                {trainingModules.required.length > 0 && (
                  <Grid item xs={12} md={4}>
                    <Box>
                      <Typography variant="h6" style={{ fontWeight: 600, marginBottom: 12, color: '#f44336' }}>
                        {getTranslation('required', preferredLanguage)}
                      </Typography>
                      {trainingModules.required.map((training, index) => (
                        <Card
                          key={index}
                          className={classes.trainingCard}
                          onClick={() => handleTrainingClick(training.modId)}
                        >
                          <Box display="flex" alignItems="center">
                            <PlayArrow style={{ color: '#f44336', marginRight: 8 }} />
                            <Typography variant="body2" style={{ fontWeight: 500 }}>
                              {training.title}
                            </Typography>
                          </Box>
                        </Card>
                      ))}
                    </Box>
                  </Grid>
                )}

                {/* Suggested Training */}
                {trainingModules.suggested.length > 0 && (
                  <Grid item xs={12} md={4}>
                    <Box>
                      <Typography variant="h6" style={{ fontWeight: 600, marginBottom: 12, color: '#0055A2' }}>
                        {getTranslation('suggested', preferredLanguage)}
                      </Typography>
                      {trainingModules.suggested.map((training, index) => (
                        <Card
                          key={index}
                          className={classes.trainingCard}
                          onClick={() => handleTrainingClick(training.modId)}
                        >
                          <Box display="flex" alignItems="center">
                            <PlayArrow style={{ color: '#0055A2', marginRight: 8 }} />
                            <Typography variant="body2" style={{ fontWeight: 500 }}>
                              {training.title}
                            </Typography>
                          </Box>
                        </Card>
                      ))}
                    </Box>
                  </Grid>
                )}

                {/* Completed Training */}
                {trainingModules.completed.length > 0 && (
                  <Grid item xs={12} md={4}>
                    <Box>
                      <Typography variant="h6" style={{ fontWeight: 600, marginBottom: 12, color: '#4caf50' }}>
                        {getTranslation('completed', preferredLanguage)}
                      </Typography>
                      {trainingModules.completed.slice(0, 5).map((training, index) => (
                        <Card
                          key={index}
                          className={classes.trainingCard}
                          onClick={() => handleTrainingClick(training.modId)}
                        >
                          <Box display="flex" alignItems="center">
                            <CheckCircleOutline style={{ color: '#4caf50', marginRight: 8 }} />
                            <Typography variant="body2" style={{ fontWeight: 500 }}>
                              {training.title}
                            </Typography>
                          </Box>
                        </Card>
                      ))}
                    </Box>
                  </Grid>
                )}
              </Grid>
            </Paper>
          </Grid>
        </Grid>
      )}

      {/* Link History Table */}
      <Paper className={classes.chartCard} style={{ marginBottom: 24 }}>
        <Typography variant="h5" style={{ fontWeight: 600, marginBottom: 16 }}>
          {getTranslation('link history', preferredLanguage)}
        </Typography>

        <Tabs
          value={tabValue}
          onChange={(e, newValue) => {
            setTabValue(newValue);
            setPage(0);
          }}
          indicatorColor="primary"
          textColor="primary"
          style={{ marginBottom: 16 }}
        >
          <Tab
            label={
              <Box display="flex" alignItems="center">
                <CheckCircle style={{ marginRight: 4, fontSize: 16, color: '#4caf50' }} />
                {getTranslation('safe', preferredLanguage)} ({tableData.safe?.length || 0})
              </Box>
            }
          />
          <Tab
            label={
              <Box display="flex" alignItems="center">
                <InfoOutlined style={{ marginRight: 4, fontSize: 16, color: '#ff9800' }} />
                {getTranslation('unknown', preferredLanguage)} ({tableData.unknown?.length || 0})
              </Box>
            }
          />
          <Tab
            label={
              <Box display="flex" alignItems="center">
                <Warning style={{ marginRight: 4, fontSize: 16, color: '#f44336' }} />
                {getTranslation('unsafe', preferredLanguage)} ({tableData.unsafe?.length || 0})
              </Box>
            }
          />
        </Tabs>

        <TableContainer className={classes.tableContainer}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>{getTranslation('Link', preferredLanguage)}</TableCell>
                <TableCell>{getTranslation('Status/Safety', preferredLanguage)}</TableCell>
                <TableCell>{getTranslation('Confidence Level', preferredLanguage)}</TableCell>
                <TableCell>{getTranslation('Date Seen', preferredLanguage)}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {getCurrentTabData().length === 0 ? (
                <TableRow>
                  <TableCell colSpan={4} align="center">
                    <Typography variant="body2" color="textSecondary">
                      {getTranslation('No links in your history yet!', preferredLanguage)}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                getCurrentTabData()
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((row, index) => (
                    <TableRow key={index} hover>
                      <TableCell
                        className={classes.linkCell}
                        onClick={() => handleLinkClick(row.linkDetails, row.safety)}
                      >
                        {row.lastLink}
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={getTranslation(safety, preferredLanguage)}
                          size="small"
                          style={{
                            backgroundColor:
                              safety === 'safe' ? '#4caf5020' : safety === 'unsafe' ? '#f4433620' : '#ff980020',
                            color: safety === 'safe' ? '#4caf50' : safety === 'unsafe' ? '#f44336' : '#ff9800',
                            fontWeight: 600,
                          }}
                        />
                      </TableCell>
                      <TableCell>{row.cl}%</TableCell>
                      <TableCell>
                        <Box display="flex" alignItems="center">
                          <Schedule style={{ fontSize: 14, marginRight: 4, color: '#666' }} />
                          {new Date(row.timeSeen).toLocaleDateString()}
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          rowsPerPageOptions={[5, 10, 25, 50]}
          component="div"
          count={getCurrentTabData().length}
          rowsPerPage={rowsPerPage}
          page={page}
          onChangePage={handleChangePage}
          onChangeRowsPerPage={handleChangeRowsPerPage}
        />
      </Paper>

      {/* Chart Section */}
      {linkHistoryData?.chartData && linkHistoryData.chartData.length > 0 && (
        <Paper className={classes.chartCard}>
          <Typography variant="h5" style={{ fontWeight: 600, marginBottom: 16 }}>
            <TrendingUp style={{ marginRight: 8, verticalAlign: 'middle' }} />
            {getTranslation('Link Activity Timeline', preferredLanguage)}
          </Typography>

          <Chart
            chartType="LineChart"
            data={[
              [
                getTranslation('Date', preferredLanguage),
                getTranslation('Safe', preferredLanguage),
                getTranslation('Unknown', preferredLanguage),
                getTranslation('Unsafe', preferredLanguage),
              ],
              ...linkHistoryData.chartData.map((item) => [
                new Date(item[0]).toLocaleDateString(),
                item[1],
                item[2],
                item[3],
              ]),
            ]}
            options={{
              curveType: 'function',
              backgroundColor: isDark ? '#1E1E1E' : '#ffffff',
              legend: {
                position: 'bottom',
                textStyle: { color: isDark ? '#e0e0e0' : '#333' },
              },
              colors: ['#4caf50', '#ff9800', '#f44336'],
              chartArea: { width: '85%', height: '70%' },
              hAxis: {
                title: getTranslation('Date', preferredLanguage),
                titleTextStyle: { color: isDark ? '#e0e0e0' : '#333', fontSize: 12 },
                textStyle: { color: isDark ? '#e0e0e0' : '#666' },
                gridlines: { color: isDark ? '#333333' : '#e0e0e0' },
                baselineColor: isDark ? '#444444' : '#ccc',
              },
              vAxis: {
                title: getTranslation('Links', preferredLanguage),
                titleTextStyle: { color: isDark ? '#e0e0e0' : '#333', fontSize: 12 },
                textStyle: { color: isDark ? '#e0e0e0' : '#666' },
                gridlines: { color: isDark ? '#333333' : '#e0e0e0' },
                baselineColor: isDark ? '#444444' : '#ccc',
                minValue: 0,
              },
              animation: {
                startup: true,
                duration: 1000,
                easing: 'out',
              },
            }}
            width="100%"
            height="400px"
          />
        </Paper>
      )}
    </div>
  );
};

export default ModernUserLinkHistory;
